import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { getAccessToken } from '@/lib/tokenStorage';

// Writes the API supports on the legacy tables (#38375, #38376). Convert
// stays 501 (it moves a row between tables), there is no edit-procedure
// dialog yet, and shipments are still mock data (#25573).
export const ANIMAL_WRITE_ACTIONS = {
  addAnimal: true,
  editAnimal: true,
  bulkEdit: true,
  addProcedures: true,
  editProcedure: false,
  convertType: false,
  addToShipment: false,
} as const;

export type AnimalTypeLabel = 'Calf' | 'LOPU Donor' | 'Donor' | 'Recipient';

export type AnimalRow = {
  id: string;
  nameTag: string;
  name: string;
  tag: string;
  registration: string;
  breed: string;
  breedId: string | null;
  rfidTag: string;
  birthDate: string;
  ranch: string;
  ownerId: string | null;
  yard: string;
  yardId: string | null;
  receivedDate: string;
  pregnant: 'Yes' | 'No' | '';
  cowHeifer: string;
  weight: number | null;
  ivfEtStatus: string;
  programCategory: string;
  housing: string;
  penId: string | null;
  shipDate: string;
  opuProgram: string;
  togId: string;
  status: string;
  animalType: AnimalTypeLabel;
  animalTypeCode: number;
  stateCode: number;
  origin: string;
  hairSample: 'Yes' | 'No' | '';
  lastDrugUsedId: string | null;
  lastDrugUsed: string;
  colorId: string | null;
  color: string;
  ocvStatusId: string | null;
  ocvStatus: string;
  recipientStatusId: string | null;
  recipientStatus: string;
  /** Picklist values, kept as strings for the select inputs. */
  client: string;
  heiferCow: string;
  sex: string;
};

export type ProcedureRow = {
  id: string;
  animalId: string;
  procedureCode: string;
  procedureName: string;
  procedureDate: string;
  description: string;
  performedByName: string;
  relatedAnimal: string;
  relatedAnimalType: AnimalTypeLabel | '';
};

export type ProcedureCatalogOption = {
  id: string;
  name: string;
  description: string;
};

type AnimalManagementResult<T> = {
  items: T[];
  hasMore: boolean;
};

// Shapes returned by GET /api/v1/animal and /api/v1/animal/procedures.
export type AnimalDto = {
  id: string;
  animalType: number;
  animalTypeName: string | null;
  togId: number | null;
  name: string | null;
  nameTag?: string | null;
  tag: string | null;
  registration: string | null;
  breedId: string | null;
  breedName: string | null;
  rfidTag: string | null;
  birthDate: string | null;
  ownerId: string | null;
  ownerName: string | null;
  yardId: string | null;
  yardName: string | null;
  penId: string | null;
  penName: string | null;
  weight: number | null;
  sex: number | null;
  pregnant: number | null;
  ivfStatusId: string | null;
  ivfStatusName?: string | null;
  programCategory: string | null;
  opuProgram: string | null;
  received: string | null;
  shipOn: string | null;
  stateCode: number;
  statusCode: number | null;
  origin?: string | null;
  hairSample?: boolean | null;
  lastDrugUsedId?: string | null;
  lastDrugUsedName?: string | null;
  recipProcedureEntryId?: string | null;
  colorId?: string | null;
  colorName?: string | null;
  ocvStatusId?: string | null;
  ocvStatusName?: string | null;
  recipientStatusId?: string | null;
  recipientStatusName?: string | null;
  client?: number | null;
  heiferCow?: number | null;
  heiferCowName?: string | null;
};

export type AnimalProcedureDto = {
  id: string;
  animalId: string;
  procedureCodeId: string | null;
  procedureCode: string | null;
  procedureName: string | null;
  procedureDate: string | null;
  description: string | null;
  performedByName: string | null;
  relatedAnimal: string | null;
  relatedAnimalType: string | null;
};

type ProcedureCodeDto = {
  id: string;
  code: string;
  description: string | null;
  shortDescription: string | null;
};

type PagedResponse<T> = { items: T[]; totalCount: number };

// The animal and procedures grid endpoints skip the full COUNT (it scans the
// legacy tables and timed out), so rows scroll on hasMore and the total comes
// from the matching /count endpoint.
type ScrollPageResponse<T> = {
  items: T[];
  hasMore: boolean;
  totalCount: number | null;
};

type CountResponse = { totalCount: number };

export const ANIMAL_TYPE_CODES: Record<AnimalTypeLabel, number> = {
  Donor: 1,
  'LOPU Donor': 2,
  Recipient: 3,
  Calf: 4,
};

const ANIMAL_TYPE_LABELS: Record<number, AnimalTypeLabel> = {
  1: 'Donor',
  2: 'LOPU Donor',
  3: 'Recipient',
  4: 'Calf',
};

// Grid column ids that differ from the API DTO property names. Used for both
// filtering and sorting via addGridQueryParams.
export const ANIMAL_FIELD_MAPPINGS: Record<string, string> = {
  breed: 'breedName',
  ranch: 'ownerName',
  yard: 'yardName',
  receivedDate: 'received',
  housing: 'penName',
  shipDate: 'shipOn',
  lastDrugUsed: 'lastDrugUsedName',
  color: 'colorName',
  ocvStatus: 'ocvStatusName',
  recipientStatus: 'recipientStatusName',
  cowHeifer: 'heiferCowName',
  ivfEtStatus: 'ivfStatusName',
  status: 'statusCode',
};

const STATE_CODE_VALUES: Record<string, string> = {
  active: '0',
  inactive: '1',
};

const parseInValues = (raw: string | null): string[] | null => {
  if (!raw) return null;
  const [op, values] = raw.includes(':') ? raw.split(/:(.*)/s) : ['eq', raw];
  if (op !== 'in' && op !== 'eq') return null;
  return values
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);
};

const parseQuickFilters = (params: URLSearchParams): AnimalTypeLabel[] =>
  (params.get('quickFilters') ?? '')
    .split(',')
    .filter((v): v is AnimalTypeLabel => v in ANIMAL_TYPE_CODES);

/**
 * Translates the grid's query params into the API contract. Returns null when
 * the filters cannot match anything, so the caller can skip the request.
 */
export const toAnimalApiParams = (
  params: URLSearchParams,
): URLSearchParams | null => {
  const out = new URLSearchParams(params);
  out.delete('quickFilters');

  const quick = parseQuickFilters(params).map((l) => ANIMAL_TYPE_CODES[l]);
  const column = parseInValues(params.get('animalType'))?.map(Number);
  let types: number[] | null = null;
  if (quick.length && column) {
    types = quick.filter((c) => column.includes(c));
  } else if (quick.length) {
    types = quick;
  } else if (column) {
    types = column;
  }
  if (types) {
    if (types.length === 0) return null;
    out.set('animalType', `in:${types.join(',')}`);
  }

  const states = parseInValues(params.get('stateCode'));
  if (states) {
    const mapped = states.map((s) => STATE_CODE_VALUES[s] ?? s);
    out.set('stateCode', `in:${mapped.join(',')}`);
  }

  return out;
};

export const toProcedureApiParams = (
  params: URLSearchParams,
): URLSearchParams => {
  const out = new URLSearchParams(params);
  out.delete('quickFilters');
  const quick = parseQuickFilters(params);
  if (quick.length) {
    out.set('relatedAnimalType', `in:${quick.join(',')}`);
  }
  return out;
};

const pregnantLabel = (value: number | null): AnimalRow['pregnant'] => {
  if (value === 1) return 'Yes';
  if (value === 0) return 'No';
  return '';
};

// CRM status reason. These custom entities only use the two defaults.
const statusReasonLabel = (value: number | null): string => {
  if (value === 1) return 'Active';
  if (value === 2) return 'Inactive';
  return value == null ? '' : String(value);
};

export const mapAnimalDto = (dto: AnimalDto): AnimalRow => ({
  id: dto.id,
  nameTag: dto.nameTag ?? dto.name ?? dto.tag ?? '',
  name: dto.name ?? '',
  tag: dto.tag ?? '',
  registration: dto.registration ?? '',
  breed: dto.breedName ?? '',
  breedId: dto.breedId,
  rfidTag: dto.rfidTag ?? '',
  birthDate: dto.birthDate ?? '',
  ranch: dto.ownerName ?? '',
  ownerId: dto.ownerId,
  yard: dto.yardName ?? '',
  yardId: dto.yardId,
  receivedDate: dto.received ?? '',
  pregnant: pregnantLabel(dto.pregnant),
  cowHeifer: dto.heiferCowName ?? '',
  weight: dto.weight,
  ivfEtStatus: dto.ivfStatusName ?? '',
  programCategory: dto.programCategory ?? '',
  housing: dto.penName ?? '',
  penId: dto.penId,
  shipDate: dto.shipOn ?? '',
  opuProgram: dto.opuProgram ?? '',
  togId: dto.togId != null ? String(dto.togId) : '',
  status: statusReasonLabel(dto.statusCode),
  animalType: ANIMAL_TYPE_LABELS[dto.animalType] ?? 'Donor',
  animalTypeCode: dto.animalType,
  stateCode: dto.stateCode,
  origin: dto.origin ?? '',
  hairSample: dto.hairSample == null ? '' : dto.hairSample ? 'Yes' : 'No',
  lastDrugUsedId: dto.lastDrugUsedId ?? null,
  lastDrugUsed: dto.lastDrugUsedName ?? '',
  colorId: dto.colorId ?? null,
  color: dto.colorName ?? '',
  ocvStatusId: dto.ocvStatusId ?? null,
  ocvStatus: dto.ocvStatusName ?? '',
  recipientStatusId: dto.recipientStatusId ?? null,
  recipientStatus: dto.recipientStatusName ?? '',
  client: dto.client != null ? String(dto.client) : '',
  heiferCow: dto.heiferCow != null ? String(dto.heiferCow) : '',
  sex: dto.sex != null ? String(dto.sex) : '',
});

export const mapProcedureDto = (dto: AnimalProcedureDto): ProcedureRow => ({
  id: dto.id,
  animalId: dto.animalId,
  procedureCode: dto.procedureCode ?? '',
  procedureName: dto.procedureName ?? '',
  procedureDate: dto.procedureDate ?? '',
  description: dto.description ?? '',
  performedByName: dto.performedByName ?? '',
  relatedAnimal: dto.relatedAnimal ?? '',
  relatedAnimalType:
    dto.relatedAnimalType && dto.relatedAnimalType in ANIMAL_TYPE_CODES
      ? (dto.relatedAnimalType as AnimalTypeLabel)
      : '',
});

async function getJson<T>(path: string, params?: URLSearchParams): Promise<T> {
  const token = getAccessToken();
  if (!token) {
    throw new Error('No access token available. Please log in.');
  }
  const query = params?.toString();
  const response = await fetch(
    `${import.meta.env.VITE_API_BASE_URL}${path}${query ? `?${query}` : ''}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    },
  );
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

const queryAnimals = async (
  params: URLSearchParams,
): Promise<AnimalManagementResult<AnimalRow>> => {
  const apiParams = toAnimalApiParams(params);
  if (!apiParams) {
    return { items: [], hasMore: false };
  }
  const result = await getJson<ScrollPageResponse<AnimalDto>>(
    '/api/v1/animal',
    apiParams,
  );
  return {
    items: result.items.map(mapAnimalDto),
    hasMore: result.hasMore,
  };
};

const queryProcedures = async (
  params: URLSearchParams,
): Promise<AnimalManagementResult<ProcedureRow>> => {
  const result = await getJson<ScrollPageResponse<AnimalProcedureDto>>(
    '/api/v1/animal/procedures',
    toProcedureApiParams(params),
  );
  return {
    items: result.items.map(mapProcedureDto),
    hasMore: result.hasMore,
  };
};

// Paging and sort don't change the total, so they're dropped from the count
// request and its cache key.
const toCountParams = (params: URLSearchParams): URLSearchParams => {
  const out = new URLSearchParams(params);
  out.delete('skip');
  out.delete('take');
  out.delete('order');
  out.sort();
  return out;
};

const COUNT_STALE_TIME = 5 * 60 * 1000;

export const animalsCountOptions = (params: URLSearchParams) => {
  const apiParams = toAnimalApiParams(params);
  const countParams = apiParams ? toCountParams(apiParams) : null;
  return {
    queryKey: ['animal-management', 'animals-count', countParams?.toString()],
    queryFn: async () =>
      countParams
        ? (await getJson<CountResponse>('/api/v1/animal/count', countParams))
            .totalCount
        : 0,
    staleTime: COUNT_STALE_TIME,
  };
};

export const proceduresCountOptions = (params: URLSearchParams) => {
  const countParams = toCountParams(toProcedureApiParams(params));
  return {
    queryKey: ['animal-management', 'procedures-count', countParams.toString()],
    queryFn: async () =>
      (
        await getJson<CountResponse>(
          '/api/v1/animal/procedures/count',
          countParams,
        )
      ).totalCount,
    staleTime: COUNT_STALE_TIME,
  };
};

export const createAnimalsOptions = (params: URLSearchParams) => ({
  queryKey: ['animal-management', 'animals', params.toString()],
  queryFn: () => queryAnimals(params),
});

export const createProceduresOptions = (params: URLSearchParams) => ({
  queryKey: ['animal-management', 'procedures', params.toString()],
  queryFn: () => queryProcedures(params),
});

export const procedureCatalogQueryOptions = () =>
  queryOptions({
    queryKey: ['animal-management', 'procedure-catalog'],
    async queryFn(): Promise<ProcedureCatalogOption[]> {
      const result = await getJson<PagedResponse<ProcedureCodeDto>>(
        '/api/procedure-code',
        new URLSearchParams({ take: '1000', order: 'code:asc' }),
      );
      return result.items.map((code) => ({
        id: code.id,
        name: `${code.code} - ${code.shortDescription ?? code.description ?? ''}`,
        description: code.description ?? '',
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

export const ANIMAL_PROCEDURES_PAGE_SIZE = 50;

export const animalProceduresQueryOptions = (animalId: string | null) =>
  infiniteQueryOptions({
    queryKey: ['animal-management', 'animal-procedures', animalId],
    enabled: Boolean(animalId),
    initialPageParam: 0,
    async queryFn({ pageParam }) {
      const result = await getJson<ScrollPageResponse<AnimalProcedureDto>>(
        '/api/v1/animal/procedures',
        new URLSearchParams({
          animalId: `eq:${animalId}`,
          skip: String(pageParam),
          take: String(ANIMAL_PROCEDURES_PAGE_SIZE),
          order: 'procedureDate:desc',
        }),
      );
      return {
        items: result.items.map(mapProcedureDto),
        // always exact when scoped to one animal
        totalCount: result.totalCount ?? 0,
      };
    },
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return lastPage.items.length > 0 && loaded < lastPage.totalCount
        ? loaded
        : undefined;
    },
  });

export * from './shipments';
export * from './writes';
